/**
 * Language support, for /docs/concepts.
 *
 * Copied from terragov/archis-benchmarks `main` (README "Soporte de lenguajes", regenerated
 * 2026-10-05 with the 0.49.0 campaign). When a campaign republishes that table, this
 * file moves with it.
 *
 * - `matrix` is the capability-matrix score on minimal fixtures
 *   (`conformance/support_matrix.json`): a ceiling, not what is seen at scale.
 * - `symbolRecall` and `callRecall` are the phase-B measurements on real repositories,
 *   against independent parsers. `null` means the language was not re-measured in that
 *   phase, not that it scored zero.
 */

export interface LanguageSupport {
  id: string
  name: string
  level: 'preview' | 'stable'
  matrix: number
  symbolRecall: number | null
  callRecall: number | null
}

export const LANGUAGES_AS_OF = '2026-10-05'

export const LANGUAGES: LanguageSupport[] = [
  { id: 'bash', name: 'Bash', level: 'preview', matrix: 96, symbolRecall: 97.0, callRecall: 94.0 },
  { id: 'c', name: 'C', level: 'preview', matrix: 96, symbolRecall: 98.8, callRecall: 98.3 },
  { id: 'cpp', name: 'C++', level: 'preview', matrix: 96, symbolRecall: 98.3, callRecall: 97.5 },
  { id: 'elixir', name: 'Elixir', level: 'preview', matrix: 96, symbolRecall: 100.0, callRecall: 99.6 },
  { id: 'haskell', name: 'Haskell', level: 'preview', matrix: 96, symbolRecall: 99.9, callRecall: 99.7 },
  { id: 'hcl', name: 'HCL', level: 'preview', matrix: 96, symbolRecall: 100.0, callRecall: 100.0 },
  { id: 'html', name: 'HTML', level: 'preview', matrix: 96, symbolRecall: 99.7, callRecall: 99.9 },
  { id: 'lua', name: 'Lua', level: 'preview', matrix: 96, symbolRecall: 100.0, callRecall: 99.8 },
  { id: 'r', name: 'R', level: 'preview', matrix: 96, symbolRecall: 100.0, callRecall: 99.5 },
  { id: 'ruby', name: 'Ruby', level: 'preview', matrix: 96, symbolRecall: 100.0, callRecall: 100.0 },
  { id: 'scala', name: 'Scala', level: 'preview', matrix: 96, symbolRecall: 99.7, callRecall: 94.9 },
  { id: 'swift', name: 'Swift', level: 'preview', matrix: 96, symbolRecall: 99.9, callRecall: 99.8 },
  { id: 'toml', name: 'TOML', level: 'preview', matrix: 96, symbolRecall: 100.0, callRecall: 100.0 },
  { id: 'zig', name: 'Zig', level: 'preview', matrix: 96, symbolRecall: 99.8, callRecall: 98.6 },
  { id: 'javascript', name: 'JavaScript', level: 'preview', matrix: 95, symbolRecall: 91.5, callRecall: 85.2 },
  { id: 'objc', name: 'Objective-C', level: 'preview', matrix: 94, symbolRecall: 99.6, callRecall: 98.1 },
  { id: 'rust', name: 'Rust', level: 'preview', matrix: 94, symbolRecall: null, callRecall: null },
  { id: 'csharp', name: 'C#', level: 'preview', matrix: 93, symbolRecall: null, callRecall: null },
  { id: 'groovy', name: 'Groovy', level: 'preview', matrix: 93, symbolRecall: 97.9, callRecall: 95.2 },
  { id: 'java', name: 'Java', level: 'preview', matrix: 93, symbolRecall: null, callRecall: 23.9 },
  { id: 'kotlin', name: 'Kotlin', level: 'stable', matrix: 93, symbolRecall: null, callRecall: 91.9 },
  { id: 'php', name: 'PHP', level: 'preview', matrix: 92, symbolRecall: null, callRecall: null },
  { id: 'python', name: 'Python', level: 'preview', matrix: 92, symbolRecall: null, callRecall: 91.0 },
  { id: 'typescript', name: 'TypeScript', level: 'preview', matrix: 91, symbolRecall: null, callRecall: 87.8 },
  { id: 'go', name: 'Go', level: 'stable', matrix: 91, symbolRecall: null, callRecall: 79.8 },
  { id: 'css', name: 'CSS', level: 'preview', matrix: 88, symbolRecall: 100.0, callRecall: 100.0 },
  { id: 'proto', name: 'Protocol Buffers', level: 'preview', matrix: 88, symbolRecall: 99.9, callRecall: 99.8 },
  { id: 'dockerfile', name: 'Dockerfile', level: 'preview', matrix: 85, symbolRecall: 100.0, callRecall: 98.9 },
  { id: 'xml', name: 'XML', level: 'preview', matrix: 85, symbolRecall: 99.9, callRecall: 99.3 },
  { id: 'yaml', name: 'YAML', level: 'preview', matrix: 85, symbolRecall: 100.0, callRecall: 99.8 },
  { id: 'graphql', name: 'GraphQL', level: 'preview', matrix: 78, symbolRecall: 89.2, callRecall: 82.7 },
]
